import { useState } from 'react';
import { DollarSign, Briefcase, FileSpreadsheet, Building2, Home, Vault, ArrowRight } from 'lucide-react';
import { AnalyzerWorkspace } from './bank-statements/analyzer/AnalyzerWorkspace';

type IncomeType = 'wage-earning' | 'self-employment' | 'bank-statements' | 'rental-income' | 'dscr' | 'asset-utilization';

interface IncomeCard {
  id: IncomeType;
  title: string;
  icon: React.ReactNode;
  description: string;
  details: string;
  disabled: boolean;
  gradient: string;
  iconBg: string;
  iconColor: string;
  borderHover: string;
  shadowHover: string;
}

export function IncomeCalculationHub() {
  const [activeCalculator, setActiveCalculator] = useState<'hub' | 'bank-statements'>('hub');
  const incomeTypes: IncomeCard[] = [
    {
      id: 'wage-earning',
      title: 'Wage Earner',
      icon: <DollarSign className="h-6 w-6" />,
      description: 'W-2 Income Calculation',
      details: 'Calculate qualifying income from W-2 wages, salaries, bonuses, and overtime',
      disabled: true,
      gradient: 'from-emerald-50 to-emerald-100/50',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      borderHover: 'hover:border-emerald-300',
      shadowHover: 'hover:shadow-emerald-100/50'
    },
    {
      id: 'self-employment',
      title: 'Self Employment',
      icon: <Briefcase className="h-6 w-6" />,
      description: 'Schedule C, K-1, 1099',
      details: 'Analyze self-employment income from tax returns and business documents',
      disabled: true,
      gradient: 'from-violet-50 to-violet-100/50',
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
      borderHover: 'hover:border-violet-300',
      shadowHover: 'hover:shadow-violet-100/50'
    },
    {
      id: 'bank-statements',
      title: 'Bank Statements',
      icon: <FileSpreadsheet className="h-6 w-6" />,
      description: '12-24 Month Analysis',
      details: 'Calculate income from bank statement deposits for non-QM loans',
      disabled: false,
      gradient: 'from-blue-50 to-blue-100/50',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      borderHover: 'hover:border-blue-300',
      shadowHover: 'hover:shadow-blue-100/50'
    },
    {
      id: 'rental-income',
      title: 'Rental Income',
      icon: <Home className="h-6 w-6" />,
      description: 'Schedule E Analysis',
      details: 'Calculate qualifying rental income from investment properties',
      disabled: true,
      gradient: 'from-amber-50 to-amber-100/50',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      borderHover: 'hover:border-amber-300',
      shadowHover: 'hover:shadow-amber-100/50'
    },
    {
      id: 'dscr',
      title: 'DSCR',
      icon: <Building2 className="h-6 w-6" />,
      description: 'Debt Service Coverage Ratio',
      details: 'Calculate DSCR for investment property qualification',
      disabled: true,
      gradient: 'from-rose-50 to-rose-100/50',
      iconBg: 'bg-rose-100',
      iconColor: 'text-rose-600',
      borderHover: 'hover:border-rose-300',
      shadowHover: 'hover:shadow-rose-100/50'
    },
    {
      id: 'asset-utilization',
      title: 'Asset Utilization',
      icon: <Vault className="h-6 w-6" />,
      description: 'Asset Depletion Method',
      details: 'Calculate qualifying income from liquid assets using depletion formulas',
      disabled: true,
      gradient: 'from-cyan-50 to-cyan-100/50',
      iconBg: 'bg-cyan-100',
      iconColor: 'text-cyan-600',
      borderHover: 'hover:border-cyan-300',
      shadowHover: 'hover:shadow-cyan-100/50'
    }
  ];

  const handleCardClick = (card: IncomeCard) => {
    if (!card.disabled && card.id === 'bank-statements') {
      setActiveCalculator('bank-statements');
    }
  };

  const handleBackToHub = () => {
    setActiveCalculator('hub');
  };

  if (activeCalculator === 'bank-statements') {
    return <AnalyzerWorkspace onBack={handleBackToHub} />;
  }

  return (
    <div className="min-h-full p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Income Calculation
        </h1>
        <p className="text-gray-600">
          Select an income type to calculate qualifying income for loan applications
        </p>
      </div>

      {/* Card Grid - 3x2 layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {incomeTypes.map((card) => (
          <button
            key={card.id}
            type="button"
            disabled={card.disabled}
            onClick={() => handleCardClick(card)}
            className={`
              group relative p-6 rounded-2xl text-left
              bg-gradient-to-br ${card.gradient}
              border border-gray-200/60
              shadow-sm
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
              transition-all duration-300 ease-out
              ${card.disabled
                ? 'cursor-not-allowed opacity-60 grayscale'
                : `cursor-pointer hover:-translate-y-1 hover:shadow-lg active:scale-[0.98] ${card.borderHover} ${card.shadowHover}`
              }
            `}
          >
            {/* Icon container */}
            <div className={`
              w-12 h-12 rounded-xl ${card.iconBg}
              flex items-center justify-center
              ${card.disabled ? '' : 'group-hover:scale-110'} transition-transform duration-300
            `}>
              <span className={card.iconColor}>{card.icon}</span>
            </div>

            {/* Status badge */}
            <span className={`
              absolute top-4 right-4 text-xs font-medium px-2.5 py-1 rounded-full
              ${card.disabled
                ? 'bg-gray-200 text-gray-500'
                : 'bg-emerald-100 text-emerald-700'}
            `}>
              {card.disabled ? 'Coming Soon' : 'Ready'}
            </span>

            {/* Content */}
            <h3 className="mt-4 text-lg font-semibold text-gray-900">
              {card.title}
            </h3>
            <p className="mt-1 text-sm font-medium text-gray-600">
              {card.description}
            </p>
            <p className="mt-2 text-sm text-gray-500 leading-relaxed">
              {card.details}
            </p>

            {/* Arrow indicator on hover */}
            {!card.disabled && (
              <ArrowRight className="
                absolute bottom-4 right-4 h-5 w-5 text-gray-400
                opacity-0 group-hover:opacity-100
                translate-x-0 group-hover:translate-x-1
                transition-all duration-300
              " />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
